import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import StartupHeader from "../../../Components/StartupHeader";
import { connect } from "react-redux";
import { signUp } from "../../../Redux/signUp/Action";
import {
  getCountry,
  getStateByCountry,
  getIndustry,
} from "../../../Services/GetFields";
import { notifyGroupLeaderSignup } from "../../../Services/Notifications";
import Select from "react-select";
import ReCAPTCHA from "react-google-recaptcha";
import { sitekey } from "../../../Utilities/Helper";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

function RegisterGroupLeader() {
  const dispatch = useDispatch();
  const history = useHistory();

  // Step state to manage the registration steps
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    groupName: "",
    subGroupName: "",
    country: "",
    state: "",
    city: "",
    orgDescription: "",
  });
  // Add state to track form validity for each step
  const [isStepValid, setIsStepValid] = useState(false);

  // Country/State selection states
  const [country, setCountry] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [countryError, setCountryError] = useState(false);

  const [countryState, setCountryState] = useState("");
  const [countryStateList, setCountryStateList] = useState([]);
  const [countryStateError, setCountryStateError] = useState(false);

  const [showStateTextBox, setShowStateTextBox] = useState(false);

  // Industry selection states
  const [industry, setIndustry] = useState([]);
  const [industryList, setIndustryList] = useState([]);

  // Terms of use and captcha states
  const [terms, setTerms] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsViewed, setTermsViewed] = useState(false);
  const [ageVerification, setAgeVerification] = useState(false);
  const [acceptTermsError, setAcceptTermsError] = useState(false);
  const [ageVerificationError, setAgeVerificationError] = useState(false);
  const [captcha, setCaptcha] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);

  // Loading state
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNext = (e) => {
    e.preventDefault();

    // If we're on the final step with personal info, submit form
    if (step === 3) {
      if (!termsViewed) {
        alert('Please view the Terms of Use before accepting them.');
        return;
      }

      if (!acceptTerms) {
        setAcceptTermsError(true);
        return;
      }

      if (!ageVerification) {
        setAgeVerificationError(true);
        return;
      }

      if (!captcha) {
        setCaptchaError(true);
        return;
      }

      // Call the API
      handleSubmit();
    } else {
      // Otherwise just move to the next step
      setStep(step + 1);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    setStep(step - 1);
  };

  // Handle industry change
  const handleIndustryChange = (selectedOptions) => {
    let industryArray = [];
    for (let i = 0; i < selectedOptions.length; i++) {
      industryArray.push({ IndustryId: selectedOptions[i].value });
    }
    setIndustry(industryArray);
  };

  // Handle terms change
  const handleTermsChange = () => {
    setAcceptTerms(!acceptTerms);
    if (!acceptTerms) {
      setAcceptTermsError(false);
    }
  };

  // Handle age verification change
  const handleAgeVerificationChange = () => {
    setAgeVerification(!ageVerification);
    if (!ageVerification) {
      setAgeVerificationError(false);
    }
  };

  // Open Terms in new window
  const openTermsInNewWindow = () => {
    window.open(`${process.env.PUBLIC_URL}/#/terms-viewer`, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    setTermsViewed(true);
  };

  // Handle captcha change
  const onCaptchaChange = (value) => {
    setCaptcha(!!value);
    if (value) {
      setCaptchaError(false);
    }
  };

  // Handle submission to API
  const handleSubmit = () => {
    setSaving(true);

    const userType = "Group Leader";

    // Create payload similar to Register.js
    const payload = {
      FirstName: formData.firstName.trim(),
      LastName: formData.lastName.trim(),
      Roles: [userType],
      Phone: "", // Phone is optional
      Email: formData.email,
      OrganizationName: formData.groupName.trim(),
      SubGroupName: formData.subGroupName.trim() || formData.groupName.trim(),
      City: formData.city.trim(),
      StateId: showStateTextBox ? 0 : countryState,
      State: showStateTextBox ? formData.state.trim() : null,
      CountryId: country,
      Password: formData.password,
      TermsOfUse: acceptTerms,
      GroupLeadersIndustries: industry,
      Organization: formData.orgDescription,
    };

    dispatch(signUp(payload, userType, true)).then((result) => {
      setSaving(false);

      // Only advance to the success screen once the signup actually succeeded;
      // signUp has already toasted the error otherwise.
      if (!result || !result.success) {
        return;
      }

      // Alert the team about the new group leader. A notification failure must
      // never affect the person signing up.
      notifyGroupLeaderSignup({
        first_name: payload.FirstName,
        last_name: payload.LastName,
        email: payload.Email,
        group_name: payload.OrganizationName,
        sub_group_name: payload.SubGroupName,
        city: payload.City,
        state: getSelectedStateName(),
        country: getSelectedCountryName(),
        industries: getSelectedIndustries().map((item) => item.label),
        org_description: payload.Organization,
      }).catch((err) => {
        console.log(err);
      });

      setStep(4); // Move to success screen
    });
  };

  // Handle country change
  const handleCountryChange = (e) => {
    setCountryStateList([]);
    const countryValue = e.target.value;
    var index = e.nativeEvent.target.selectedIndex;
    const countryText = e.nativeEvent.target[index].text;

    setCountry(countryValue);
    setFormData({ ...formData, country: countryValue });

    if (countryText === "Other") {
      setShowStateTextBox(true);
      setCountryState("");
      setFormData({ ...formData, country: countryValue, state: "" });
    } else {
      setShowStateTextBox(false);
    }

    if (countryValue === "0") {
      setCountryError(true);
    } else {
      setCountryError(false);
      getStateByCountry(countryValue)
        .then((res) => {
          const result = JSON.parse(res.response);
          setCountryStateList(result);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  // Handle state change
  const handleStateChange = (e) => {
    const stateValue = e.target.value;
    setCountryState(stateValue);
    setFormData({ ...formData, state: stateValue });

    if (stateValue === "0") {
      setCountryStateError(true);
    } else {
      setCountryStateError(false);
    }
  };

  // Validate form fields based on current step
  useEffect(() => {
    let valid = false;

    if (step === 1) {
      valid = formData.groupName.trim() !== "";
    } else if (step === 2) {
      if (showStateTextBox) {
        valid =
          formData.country.trim() !== "" &&
          formData.country !== "0" &&
          formData.state.trim() !== "" &&
          formData.city.trim() !== "" &&
          formData.orgDescription.trim() !== "";
      } else {
        valid =
          formData.country.trim() !== "" &&
          formData.country !== "0" &&
          formData.state.trim() !== "" &&
          formData.state !== "0" &&
          formData.city.trim() !== "" &&
          formData.orgDescription.trim() !== "";
      }
    } else if (step === 3) {
      const passwordRegex =
        /^(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z])[a-zA-Z0-9!@#$%^&*]{8,}$/;
      const validEmail = /\S+@\S+\.\S+/.test(formData.email);
      const validPassword = passwordRegex.test(formData.password);
      const passwordsMatch = formData.password === formData.confirmPassword;

      valid =
        formData.firstName.trim() !== "" &&
        formData.lastName.trim() !== "" &&
        formData.email.trim() !== "" &&
        validEmail &&
        formData.password.trim() !== "" &&
        formData.confirmPassword.trim() !== "" &&
        validPassword &&
        passwordsMatch;
    }

    setIsStepValid(valid);
  }, [step, formData, showStateTextBox]);

  // Load country list and industry list on component mount
  useEffect(() => {
    async function getAllCountryList() {
      getCountry()
        .then((res) => {
          const result = JSON.parse(res.response);
          setCountryList(result);
        })
        .catch((err) => {
          console.log(err);
        });
    }

    async function getAllIndustryList() {
      getIndustry()
        .then((res) => {
          const result = JSON.parse(res.response);
          let industryArray = [];
          for (let i = 0; i < result.length; i++) {
            industryArray.push({
              label: result[i].industryName,
              value: result[i].industryId,
            });
          }
          setIndustryList(industryArray);
        })
        .catch((err) => {
          console.log(err);
        });
    }

    getAllCountryList();
    getAllIndustryList();
  }, []);

  useEffect(() => {
    document.body.classList.add("home-page");
    return () => {
      document.body.classList.remove("home-page");
    };
  }, []);

  // Helper function to render industries for display
  const getSelectedIndustries = () => {
    let industryIds = industry.map((item) => item.IndustryId);
    return industryList.filter((item) => industryIds.includes(item.value));
  };

  // The form submits country/state ids; these resolve the display names for
  // the team signup notification.
  const getSelectedCountryName = () => {
    const match = countryList.find(
      (item) => String(item.countryId) === String(country)
    );
    return match ? match.countryName : "";
  };

  const getSelectedStateName = () => {
    if (showStateTextBox) {
      return formData.state.trim();
    }
    const match = countryStateList.find(
      (item) => String(item.stateId) === String(countryState)
    );
    return match ? match.stateName : "";
  };

  return (
    <div className="register-group-leader">
      <StartupHeader />
      {!terms ? (
        <div className="main-content">
          <div
            className="main-banner"
            style={{ backgroundColor: "rgba(0, 0, 0, 0)" }}
          >
            <div className="dashboard-wrapper">
              <div className="container">
                <div className="dashboard-box dashboard-col-bg public-wrapper outline-form">
                  {step === 1 && (
                    <div className="p-5">
                      <h1 className="mb-4">Group Registration - Step 1 of 3</h1>
                      <p className="mb-4">
                        <b>Welcome! Let's start by naming your group.</b>
                        <br />
                        Enter your organization's name and if you're part of a
                        "sub group", please provide that name as well. The group
                        name should be your main organization, and the sub-group
                        might be a specific location, department, or team within
                        your organization.
                      </p>
                      <Form noValidate>
                        <Form.Group controlId="groupName" className="mb-3">
                          <Form.Label>Group Name *</Form.Label>
                          <Form.Control
                            required
                            name="groupName"
                            type="text"
                            placeholder="Enter your group name"
                            value={formData.groupName}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group controlId="subGroupName" className="mb-4">
                          <Form.Label>Sub Group Name</Form.Label>
                          <Form.Control
                            name="subGroupName"
                            type="text"
                            placeholder="Enter your subgroup name"
                            value={formData.subGroupName}
                            onChange={handleChange}
                          />
                        </Form.Group>

                        <div className="d-flex flex-row-reverse justify-content-between">
                          <Button
                            variant="primary"
                            type="submit"
                            onClick={handleNext}
                            disabled={!isStepValid}
                          >
                            Next
                          </Button>
                        </div>
                      </Form>
                    </div>
                  )}
                  {step === 2 && (
                    <div className="p-5">
                      <h1 className="mb-4">Group Registration - Step 2 of 3</h1>

                      <p className="mb-4">
                        <b>Group location and a brief introduction</b>
                        <br />
                        Please enter the country, state, and city where your
                        group is located. You can also include a short
                        description to help others understand your
                        organization's purpose and who it supports.
                      </p>

                      <Form noValidate>
                        <Row>
                          <Col className="dd">
                            <Form.Group controlId="country" className="mb-3">
                              <Form.Label>Country *</Form.Label>
                              <Form.Control
                                as="select"
                                name="country"
                                value={country}
                                onChange={handleCountryChange}
                                className="inputclass form-control"
                              >
                                <option value="0">Select Country *</option>
                                {countryList.map((item) => (
                                  <option
                                    key={item.countryId}
                                    value={item.countryId}
                                  >
                                    {item.countryName}
                                  </option>
                                ))}
                              </Form.Control>
                              {countryError && (
                                <p style={{ color: "red" }}>
                                  Country is required
                                </p>
                              )}
                            </Form.Group>
                          </Col>
                          {!showStateTextBox && (
                            <Col className="dd">
                              <Form.Group controlId="state" className="mb-3">
                                <Form.Label>
                                  {country == 2 ? "Province *" : "State *"}
                                </Form.Label>
                                <Form.Control
                                  as="select"
                                  name="state"
                                  value={countryState}
                                  onChange={handleStateChange}
                                  className="inputclass form-control"
                                >
                                  <option value="0">
                                    {country == 2
                                      ? "Select Province *"
                                      : "Select State *"}
                                  </option>
                                  {countryStateList.map((item) => (
                                    <option
                                      key={item.stateId}
                                      value={item.stateId}
                                    >
                                      {item.stateName}
                                    </option>
                                  ))}
                                </Form.Control>
                                {countryStateError && (
                                  <p style={{ color: "red" }}>
                                    {country == 2
                                      ? "Province is required"
                                      : "State is required"}
                                  </p>
                                )}
                              </Form.Group>
                            </Col>
                          )}

                          {showStateTextBox && (
                            <Col md={6}>
                              <Form.Group
                                controlId="stateText"
                                className="mb-3"
                              >
                                <Form.Label>State/Province *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="state"
                                  placeholder="Enter State/Province"
                                  maxLength={30}
                                  value={formData.state}
                                  onChange={handleChange}
                                  className="inputclass form-control"
                                />
                                {countryStateError && (
                                  <p style={{ color: "red" }}>
                                    State/Province is required
                                  </p>
                                )}
                              </Form.Group>
                            </Col>
                          )}
                        </Row>

                        <Form.Group controlId="city" className="mb-4">
                          <Form.Label>City *</Form.Label>
                          <Form.Control
                            required
                            name="city"
                            type="text"
                            placeholder="Enter city"
                            value={formData.city}
                            onChange={handleChange}
                          />
                        </Form.Group>

                        <Form.Group controlId="orgDescription" className="mb-3">
                          <Form.Label>Organization Description *</Form.Label>
                          <Form.Control
                            required
                            name="orgDescription"
                            as="textarea"
                            rows={3}
                            placeholder="Enter a short description of your organization"
                            value={formData.orgDescription}
                            onChange={handleChange}
                          />
                        </Form.Group>

                        <div className="d-flex flex-row justify-content-between">
                          <Button
                            variant="secondary"
                            onClick={handleBack}
                            className="me-2"
                          >
                            Back
                          </Button>
                          <Button
                            variant="primary"
                            type="submit"
                            onClick={handleNext}
                            disabled={!isStepValid}
                          >
                            Next
                          </Button>
                        </div>
                      </Form>
                    </div>
                  )}
                  {step === 3 && (
                    <div className="p-5">
                      <h1 className="mb-4">Group Registration - Step 3 of 3</h1>
                      <Form noValidate>
                        <p className="mb-4">
                          <b>Who's leading this group?</b>
                          <br />
                          We'll use this information to create the account and
                          assign the Group Leader. Please enter their name,
                          email, and a password for signing in.
                        </p>

                        <Form.Group controlId="firstName" className="mb-3">
                          <Form.Label>First Name *</Form.Label>
                          <Form.Control
                            required
                            name="firstName"
                            type="text"
                            placeholder="Enter First Name"
                            value={formData.firstName}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group controlId="lastName" className="mb-3">
                          <Form.Label>Last Name *</Form.Label>
                          <Form.Control
                            required
                            name="lastName"
                            type="text"
                            placeholder="Enter Last Name"
                            value={formData.lastName}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group controlId="email" className="mb-3">
                          <Form.Label>Email *</Form.Label>
                          <Form.Control
                            required
                            name="email"
                            type="email"
                            placeholder="Enter Email"
                            value={formData.email}
                            onChange={handleChange}
                          />
                          {formData.email &&
                            !/\S+@\S+\.\S+/.test(formData.email) && (
                              <p style={{ color: "red" }}>
                                Please enter a valid email address
                              </p>
                            )}
                        </Form.Group>
                        <Form.Group controlId="password" className="mb-3">
                          <Form.Label>Password *</Form.Label>
                          <Form.Control
                            required
                            minLength={8}
                            name="password"
                            type="password"
                            placeholder="Enter Password"
                            value={formData.password}
                            onChange={handleChange}
                          />
                          {formData.password &&
                            !/^(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z])[a-zA-Z0-9!@#$%^&*]{8,}$/.test(
                              formData.password
                            ) && (
                              <p style={{ color: "red" }}>
                                Password must be at least 8 characters and
                                include uppercase, lowercase, number, and
                                special character
                              </p>
                            )}
                        </Form.Group>
                        <Form.Group
                          controlId="confirmPassword"
                          className="mb-3"
                        >
                          <Form.Label>Confirm Password *</Form.Label>
                          <Form.Control
                            name="confirmPassword"
                            type="password"
                            placeholder="Re-enter Password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                          />
                          {formData.confirmPassword &&
                            formData.password !== formData.confirmPassword && (
                              <p style={{ color: "red" }}>
                                Passwords do not match
                              </p>
                            )}
                        </Form.Group>

                        {/* <Form.Group controlId="industry" className="mb-4">
                          <Form.Label>Industries</Form.Label>
                          <Select
                            placeholder="Select Industries"
                            options={industryList}
                            value={getSelectedIndustries()}
                            onChange={handleIndustryChange}
                            isMulti
                            className="basic-multi-select"
                          />
                        </Form.Group> */}

                        <Form.Group controlId="acceptTerms" className="mb-3">
                          <div className="custom-control custom-checkbox">
                            <input
                              className="custom-control-input"
                              type="checkbox"
                              checked={acceptTerms}
                              onChange={handleTermsChange}
                              id="termsCheckbox"
                              disabled={!termsViewed}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="termsCheckbox"
                            >
                              I accept the JobInfoNetwork's &nbsp;
                              <button
                                type="button"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#0d6efd',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  padding: 0
                                }}
                                onClick={openTermsInNewWindow}
                              >
                                Terms of use
                              </button>
                              {!termsViewed && (
                                <span style={{ color: 'red', fontSize: '12px', display: 'block' }}>
                                  Please view the Terms of Use first
                                </span>
                              )}
                            </label>
                          </div>
                          {acceptTermsError && (
                            <p style={{ color: "red" }}>
                              Please accept the Terms of Use
                            </p>
                          )}
                        </Form.Group>

                        <Form.Group controlId="ageVerification" className="mb-3">
                          <div className="custom-control custom-checkbox">
                            <input
                              className="custom-control-input"
                              type="checkbox"
                              checked={ageVerification}
                              onChange={handleAgeVerificationChange}
                              id="ageVerificationCheckbox"
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="ageVerificationCheckbox"
                            >
                              I confirm that I am at least 18 years old
                            </label>
                          </div>
                          {ageVerificationError && (
                            <p style={{ color: "red" }}>
                              Please confirm that you are at least 18 years old
                            </p>
                          )}
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <ReCAPTCHA
                            sitekey={sitekey}
                            onChange={onCaptchaChange}
                          />
                          {captchaError && (
                            <p style={{ color: "red" }}>
                              Please complete the CAPTCHA
                            </p>
                          )}
                        </Form.Group>

                        <div className="d-flex flex-row justify-content-between">
                          <Button
                            variant="secondary"
                            onClick={handleBack}
                            className="me-2"
                          >
                            Back
                          </Button>
                          <Button
                            variant="primary"
                            type="submit"
                            onClick={handleNext}
                            disabled={!isStepValid || saving}
                          >
                            {saving ? "Submitting..." : "Submit"}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  )}

                  {step === 4 && (
                    <div className="p-5">
                      <h1 className="mb-4">Group Registration - Complete</h1>
                      <p>
                        Thank you for registering your group with
                        JobInfoNetwork!
                      </p>
                      <p>
                        Your account has been created successfully. You will
                        receive a confirmation email shortly.
                      </p>
                      <p>
                        From your account, you'll be able to manage your group,
                        create subgroups, and confirm that “Support Team
                        Members” (employed members) of your group are assisting
                        job seekers within your group.
                      </p>

                      <div className="d-flex justify-content-center mt-4">
                        <Button
                          variant="primary"
                          href={`${process.env.PUBLIC_URL}/login`}
                        >
                          Go to Login
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="main-content">
          <div className="main-banner">
            <div className="dashboard-wrapper resumes-page">
              <div className="container">
                <div className="dashboard-box dashboard-col-bg public-wrapper outline-form terms-page">
                  <Form noValidate>
                    <Row>
                      <Col md={10} className="mx-auto">
                        <h1 className="mb-5">Terms of Use</h1>
                        <iframe
                          src="Terms.html"
                          frameBorder="0"
                          className="h-100v"
                          height="100%"
                          width="100%"
                          title="Terms of Use"
                        ></iframe>

                        <Row>
                          <Col md={8} className="square-radius mt-5">
                            <Button
                              variant="outline-primary"
                              onClick={() => setTerms(false)}
                              className="d-flex align-items-center"
                            >
                              <span className="icon icon-arrow-left mr-2"></span>
                              Back
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default connect(null, { signUp })(RegisterGroupLeader);
